import FilePreviewComponent from '@/components/probaworkflows_base_components/FilePreviewComponent/FilePreviewComponent.vue';
import {
  CREATE_CHECKLIST_EVALUATION_RESULT,
  GET_CHECKLIST_ITEMS_WITH_ANSWER_STATUS,
  get_headers
} from '@/utils/constants/constants';
import alert_mixin from '@/utils/mixins/alert_mixin';
import axios from 'axios';

export default {
  name: 'ComplianceChecklist',
  mixins: [alert_mixin],
  components: {
    FilePreviewComponent,
  },
  props: {
    entity_profile_id: {
      type: [String, Number],
      required: true
    }
  },
  data: () => ({
    checklist_items: [],
    is_loading: false,
    is_rerunning_checks: false,
    preview_modal: false,
    preview_url: null,
    preview_file_name: ''
  }),

  computed: {
    completed_count() {
      const count = this.checklist_items.filter(item => {
        if (item.requirement_statuses && item.requirement_statuses.length > 0) {
          const all_met = item.requirement_statuses.every(req_status => req_status.is_met);
          return all_met;
        }
        return false;
      }).length;

      return count;
    },

    completion_percentage() {
      if (this.checklist_items.length === 0) return 0;
      return Math.round((this.completed_count / this.checklist_items.length) * 100);
    },

    progress_bar_style() {
      const progress_bar_style = {
        width: `${this.completion_percentage}%`,
        '--hue': this.progress_bar_hue
      };
      return progress_bar_style;
    },

    progress_bar_hue() {
      return 120 * (this.completion_percentage / 100);
    },

    progress_color() {
      if (this.completion_percentage < 33) return 'red';
      if (this.completion_percentage < 66) return 'orange';
      return 'green';
    }
  },

  watch: {
    checklist_items: {
      deep: true,
      handler() {
        this.update_completion_status();
      }
    },
    entity_profile_id() {
      this.load_checklist_items();
    }
  },

  mounted() {
    this.load_checklist_items();

  },

  methods: {
    async load_checklist_items() {
      if (!this.entity_profile_id) {
        return;
      }

      if (isNaN(Number(this.entity_profile_id))) {
        return;
      }

      this.is_loading = true;
      try {
        const response = await axios.get(GET_CHECKLIST_ITEMS_WITH_ANSWER_STATUS, {
          headers: get_headers(),
          params: { entity_id: this.entity_profile_id }
        });
        if (response.data.status === 'success') {
          this.checklist_items = response.data.data.map(item => {
            return {
              association_id: item.association_id,
              id: item.id,
              title: item.name,
              is_open: true,
              is_completed: item.has_answer,
              is_evaluated: item.is_evaluated || false,
              evaluation_result_id: item.evaluation_result_id || null,
              evaluation_result: item.evaluation_result ?? null,
              requirement_types: item.requirement_types,
              requirement_statuses: item.requirement_statuses || [],
              target_questions: item.target_questions.map(question => ({
                id: question.id,
                text: question.question,
                question_type: question.question_type,
                is_answered: question.is_answered,
                answer: question.answer || null,
                is_applicable: question.is_applicable !== false, // default true
                dependency_meta: question.dependency_meta || null
              }))
            };
          });

          this.$emit('compliance-loaded', this.checklist_items.length > 0);

        } else {
          this.show_error('Failed to load checklist items');
        }
      }  finally {
        this.is_loading = false;
      }
    },

    get_applicable_questions(questions) {
      return (questions || []).filter(q => q.is_applicable);
    },

    get_not_applicable_questions(questions) {
      return (questions || []).filter(q => !q.is_applicable);
    },

    get_answered_applicable_count(questions) {
      return this.get_applicable_questions(questions).filter(q => q.is_answered).length;
    },


    get_requirement_type_class(req_status) {
      // Base classes for all badges
      const base_classes = 'border border-2';

      if (!req_status.is_met) {
        return `${base_classes} bg-danger bg-opacity-10 border-danger text-danger`;
      }

      const type_lower = req_status.type.toLowerCase().replace(/\s+/g, '-');

      const class_map = {
        'answered': `${base_classes} bg-success bg-opacity-10 border-success text-success`,
        'manual-verification': `${base_classes} bg-info bg-opacity-10 border-info text-info`,
        'automatic-verification': `${base_classes} bg-primary bg-opacity-10 border-primary text-primary`,
        'requested-info-feedback': `${base_classes} bg-warning bg-opacity-10 border-warning text-warning`,
        'requested-information-feedback': `${base_classes} bg-warning bg-opacity-10 border-warning text-warning`,
      };

      if (Reflect.has(class_map, type_lower)) {
        return Reflect.get(class_map, type_lower);
      }
      return `${base_classes} bg-secondary bg-opacity-10 border-secondary text-secondary`;
    },

    get_requirement_type_icon(req_status) {
      const type_lower = req_status.type.toLowerCase().replace(/\s+/g, '-');

      const icon_map = {
        'answered': 'bi bi-chat-check-fill',
        'manual-verification': 'bi bi-shield-check',
        'automatic-verification': 'bi bi-shield-fill-check',
        'requested-info-feedback': 'bi bi-chat-dots-fill',
        'requested-information-feedback': 'bi bi-chat-dots-fill',
      };

      if (Reflect.has(icon_map, type_lower)) {
        return Reflect.get(icon_map, type_lower);
      }
      return 'bi bi-tag-fill';
    },

    get_verification_type(req_status) {
      // For verification requirements, get the verification type from backend
      const type_lower = req_status.type.toLowerCase();
      if ((type_lower.includes('verification')) && req_status.verification_type) {
        return `(${req_status.verification_type})`;
      }
      return '';
    },

    preview_file(file_url) {
      this.preview_url = file_url;
      this.preview_file_name = this.get_file_name_from_url(file_url);
      this.preview_modal = true;
    },

    get_file_name_from_url(url) {
      const decoded = decodeURIComponent(url);
      const clean = decoded.split('?')[0];
      return clean.split('/').pop();
    },


    close_the_file_preview() {
      this.preview_modal = false;
      this.preview_url = null;
      this.preview_file_name = '';
    },


    toggle_section(index) {
      const item = Reflect.get(this.checklist_items, index);
      if (item) {
        Reflect.set(item, 'is_open', !Reflect.get(item, 'is_open'));
      }
    },

    update_completion_status() {
      this.checklist_items.forEach(item => {
        if (item.requirement_statuses && item.requirement_statuses.length > 0) {
          Reflect.set(item, 'is_completed', item.requirement_statuses.every(req_status => req_status.is_met));
        } else {
          Reflect.set(item, 'is_completed', false);
        }
      });
    },

    async re_run_checks() {
      // Only allow re-running when completion is 100%
      if (this.completion_percentage !== 100) return;
      this.is_rerunning_checks = true;
      try {
        await this.load_checklist_items();
        this.show_success('Re-ran checks and refreshed checklist');
        this.$emit('checks-rerun', { entity_profile_id: this.entity_profile_id });
      } catch (err) {
        this.show_error('Failed to re-run checks');
      } finally {
        this.is_rerunning_checks = false;
      }
    },

    get_answered_count(questions) {
      return questions.filter(question => question.is_answered).length;
    },

    format_answer(answer) {
      if (answer === null || answer === undefined) return 'N/A';
      if (typeof answer === 'boolean') return answer ? 'Yes' : 'No';
      if (typeof answer === 'string' && answer.length > 50) {
        return answer.substring(0, 50) + '...';
      }
      return String(answer);
    },

    async submit_evaluation_result(checklist_item_association_id, result) {
      const payload = {
        checklist_item_association_id,
        entity_profile_id: this.entity_profile_id,
        result
      };

      try {
        const response = await axios.post(CREATE_CHECKLIST_EVALUATION_RESULT, payload, {
          headers: get_headers()
        });

        if (response.data.status === 'success') {
          const index = this.checklist_items.findIndex(
            i => Number(i.association_id) === Number(checklist_item_association_id)
          );
          if (index !== -1) {
            // safest: replace the object so Vue re-renders for sure
            const updated = {
              ...Reflect.get(this.checklist_items, index),
              is_evaluated: true,
              evaluation_result_id: Reflect.get(response.data, 'result_id') || 
                Reflect.get(Reflect.get(this.checklist_items, index), 'evaluation_result_id'),
            };
            this.checklist_items.splice(index, 1, updated);
          }
          this.show_success('Evaluation result submitted successfully');
        } else {
          this.show_error(response.data.message || 'Failed to submit evaluation result');
        }
      } catch (e) {
        this.show_error('Failed to submit evaluation result');
      }
    }

  }
};